import { useEffect, useState } from "react";
import api from "../../api/api";
import { toast } from "react-toastify";
import { IoIosArrowBack } from "react-icons/io";
import { useNavigate } from "react-router-dom";
import { FaSpinner } from "react-icons/fa";
import DatePicker from "react-date-picker";
import "react-date-picker/dist/DatePicker.css";
import "react-calendar/dist/Calendar.css";
import { useTranslation } from "react-i18next";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import { useProfileStore } from "../../store/useUserStore";

const EditProfile = () => {
  const [parentData, setParentData] = useState<any>({
    name: "",
    email: "",
    ic: "",
    birthday: new Date(),
    profile_pic: "/assets/img/default-profile.jpg",
    phone: "",
    gender: "",
    address1: "",
    address2: "",
    city: "",
    state: 0,
    post_code: "",
  });
  const navigate = useNavigate();
  const [innerLoading, setInnerLoading] = useState(false);
  const { t } = useTranslation();
  const { setProfile, startProgress, tickProgress } = useProfileStore((state) => state);
  const [showDetails, setShowDetails] = useState(false);
  const [statesList, setStatesList] = useState<
    { id: number; state_name: string }[]
  >([]);

  useEffect(() => {
    getParentDetail();
    getStates();
  }, []);

  const getParentDetail = async () => {
    startProgress(1);
    api
      .get(`${process.env.REACT_APP_API_BASE_URL}/user/profile`, {
        headers: {
          Authorization: localStorage.getItem("parent_token"),
          "Content-Type": "application/json",
        },
      })
      .then((res) => {
        console.log("parent", res.data.data);
        setParentData({
          name: res.data.data.name,
          email: res.data.data.email,
          phone: res.data.data.phone,
          ic: res.data.data.ic_number,
          gender: res.data.data.gender || "M",
          birthday: res.data.data.birthday || new Date(),
          profile_pic:
            res.data.data.profile_pic || "/assets/img/default-profile.jpg",
          address1: res.data.data.address1 || "",
          address2: res.data.data.address2 || "",
          city: res.data.data.city || "",
          state: res.data.data.state?.id || "",
          post_code: res.data.data.post_code || "",
        });
      })
      .catch((err) => {
        console.log("err", err);
        toast.error("Server Error");
      })
      .finally(() => {
        tickProgress();
      });
  };

  const getStates = async () => {
    try {
      const response = await api.get(
        `${process.env.REACT_APP_API_BASE_URL}/state`,
      );
      setStatesList(response.data.result.data || []);
    } catch (err) {
      console.error("Failed to fetch states:", err);
    }
  };

  const updateParent = async (values: any) => {
    if (!values.phone) return toast.warn("Phone number required");

    startProgress(1);
    const date = new Date(values.birthday);
    const formattedDate = date.toLocaleDateString("en-CA");

    let payload: any = {
      name: values.name,
      ic_number: values.ic,
      birthday: formattedDate,
      // "email": parentData.email,
      gender: values.gender,
      phone: values.phone,
    };

    // console.log("payload", values.gender)
    // Check if profile_pic is Base64 data
    if (parentData.profile_pic.startsWith("data:")) {
      payload["profile_pic"] = parentData.profile_pic;
    }

    if (parentData.address1 && !values.address1) {
      payload.address1 = null;
    } else if (values.address1) {
      payload.address1 = values.address1;
    }

    if (parentData.address2 && !values.address2) {
      payload.address2 = null;
    } else if (values.address2) {
      payload.address2 = values.address2;
    }

    if (parentData.city && !values.city) {
      payload.city = null;
    } else if (values.city) {
      payload.city = values.city;
    }

    if (parentData.post_code && !values.post_code) {
      payload.post_code = null;
    } else if (values.post_code) {
      payload.post_code = values.post_code;
    }

    if (parentData.country && !values.country) {
      payload.country = null;
    } else if (values.country) {
      payload.country = values.country;
    }

    if (parentData.state && Number(values.state) === 0) {
      payload.state = null;
    } else if (Number(values.state) !== 0) {
      payload.state = Number(values.state);
    }
    // if (Number(values.state) !== 0) payload.state = Number(values.state)

    api
      .put(`${process.env.REACT_APP_API_BASE_URL}/user/profile`, payload, {
        headers: {
          Authorization: localStorage.getItem("token"),
          "Content-Type": "application/json",
        },
      })
      .then((res) => {
        console.log("res", res.data);
        setProfile({
          name: values.name,
          ic_number: values.ic,
          birthday: formattedDate,
          phone: values.phone,
        });

        if (payload["profile_pic"]) {
          setProfile({
            profile_pic: parentData.profile_pic,
          });
        }

        toast.success("Updated Succesfully");
      })
      .catch((err) => {
        console.log("err", err);
        toast.error("Server Error");
      })
      .finally(() => {
        tickProgress();
        getParentDetail();
      });
  };

  const handleImageChange = (e: any) => {
    const file = e.target.files[0];
    if (file) {
      const validExtensions = ["image/jpeg", "image/png", "image/jpg"];
      if (!validExtensions.includes(file.type))
        return toast.error("Only JPG and PNG images are allowed.");

      const maxFileSize = 3 * 1024 * 1024; // 1 MB in bytes
      if (file.size > maxFileSize)
        return toast.error("Image size should not exceed 3 MB");

      const reader = new FileReader();
      reader.onloadend = () => {
        setParentData((prevData: any) => ({
          ...prevData,
          profile_pic: reader.result,
        }));
      };
      reader.readAsDataURL(file);
    }
  };

  const validationSchema = Yup.object().shape({
    name: Yup.string().required(t("validation.required")),
    phone: Yup.string()
      .matches(/^\d+$/, t("validation.invalidPhone"))
      .required(t("validation.required")),
    ic: Yup.string().required(t("validation.required")),
    birthday: Yup.date().required(t("validation.required")),
    gender: Yup.string().required(t("validation.required")),
  });

  return (
    <>
      <div className="pb-28">
        <div className="mb-2 grid grid-cols-4 px-2">
          <div>
            <IoIosArrowBack
              size={28}
              color="#000"
              onClick={() => navigate("/profile")}
            />
          </div>
          <div className="text-center col-span-2 text-lg font-medium">
            {t("profile.edit.title")}
          </div>
          <div></div>
        </div>

        <div className="mt-7">
          <div className="flex flex-col items-center">
            <div
              className="relative"
              style={{
                height: "120px",
                width: "8rem",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              <img
                src={parentData.profile_pic || "/default-profile.png"}
                className="img-fluid"
                alt={t("profile.edit.altProfileImage")}
                style={{
                  height: "100%",
                  width: "100%",
                  borderRadius: "50%",
                  objectFit: "cover",
                }}
              />
              <button
                className="absolute bottom-1 right-1 bg-theme text-white rounded-full px-2.5 py-0.5 shadow-md"
                onClick={() => document.getElementById("fileInput")!.click()}
              >
                {" "}
                +{" "}
              </button>
              <input
                id="fileInput"
                type="file"
                accept=".jpg,.jpeg,.png"
                style={{ display: "none" }}
                onChange={handleImageChange}
              />
            </div>
            <div style={{ marginTop: "20px", marginBottom: "20px" }}>
              <p className="name">{t("profile.edit.uploadPicture")}</p>
            </div>
          </div>

          <Formik
            enableReinitialize
            initialValues={{
              name: parentData.name || "",
              phone: parentData.phone || "",
              ic: parentData.ic || "",
              birthday: parentData.birthday
                ? new Date(parentData.birthday)
                : null,
              gender: parentData.gender || "M",
              address1: parentData.address1 || "",
              address2: parentData.address2 || "",
              city: parentData.city || "",
              state: parentData.state || "",
              post_code: parentData.post_code || "",
            }}
            validationSchema={validationSchema}
            onSubmit={(values) => {
              updateParent(values);
            }}
          >
            {({ values, setFieldValue, handleSubmit }) => (
              <Form className="py-4 px-3 shadow-lg rounded-2xl">
                <div className="mb-3">
                  <label className="text-md font-medium mb-2">
                    {t("profile.edit.fullName")}
                  </label>
                  <div className="flex items-center gap-2">
                    <Field
                      type="text"
                      name="name"
                      placeholder={t("profile.edit.fullName")}
                      disabled={!showDetails}
                      className={`flex-grow px-4 py-3 border rounded-full text-md focus:outline-none focus:ring-2 focus:ring-theme ${
                        !showDetails
                          ? "bg-gray-100 text-gray-500 cursor-not-allowed"
                          : ""
                      }`}
                    />
                    <button
                      type="button"
                      onClick={() => setShowDetails(!showDetails)}
                      className="text-lg px-3 py-2 bg-white hover:bg-gray-100"
                    >
                      {showDetails ? "▲" : "▼"}
                    </button>
                  </div>
                  <ErrorMessage
                    name="name"
                    component="div"
                    className="ml-4 pt-1 text-red-500 text-sm"
                  />
                </div>

                {showDetails && (
                  <>
                    <div className="mb-3">
                      <label className="text-md font-medium mb-2">
                        {t("profile.edit.email")}
                      </label>
                      <input
                        type="text"
                        disabled
                        value={parentData.email}
                        className="w-full px-4 py-3 border rounded-full text-md focus:outline-none focus:ring-2 focus:ring-theme"
                      />
                    </div>

                    <div className="mb-3">
                      <label className="text-md font-medium mb-2">
                        {t("profile.edit.phone")}
                      </label>
                      <Field
                        type="text"
                        name="phone"
                        placeholder={t("profile.edit.phone")}
                        className="w-full px-4 py-3 border rounded-full text-md focus:outline-none focus:ring-2 focus:ring-theme"
                        onChange={(e: any) => {
                          const numericValue = e.target.value.replace(
                            /\D/g,
                            "",
                          );
                          setFieldValue("phone", numericValue, true);
                        }}
                      />
                      <ErrorMessage
                        name="phone"
                        component="div"
                        className="ml-4 pt-1 text-red-500 text-sm"
                      />
                    </div>

                    <div className="mb-3">
                      <label className="text-md font-medium mb-2">
                        {t("profile.edit.identificationNumber")}
                      </label>
                      <Field
                        type="text"
                        name="ic"
                        placeholder={t("profile.edit.identificationNumber")}
                        className="w-full px-4 py-3 border rounded-full text-md focus:outline-none focus:ring-2 focus:ring-theme"
                        onCompositionEnd={(e: any) => {
                          const numericValue = e.target.value.replace(
                            /\D/g,
                            "",
                          );
                          setFieldValue("ic", numericValue, true);
                        }}
                      />
                      <ErrorMessage
                        name="ic"
                        component="div"
                        className="ml-4 pt-1 text-red-500 text-sm"
                      />
                    </div>

                    <div className="mb-3">
                      <label className="text-md font-medium mb-2">
                        {t("profile.edit.birthday")}
                      </label>
                      <Field name="birthday">
                        {({ field, form }: any) => (
                          <>
                            <DatePicker
                              onChange={(date) =>
                                form.setFieldValue("birthday", date)
                              }
                              value={field.value ? new Date(field.value) : null}
                              format="yyyy-MM-dd"
                              className="min-w-full px-4 py-3 border rounded-full text-md focus:outline-none focus:ring-2 focus:ring-theme"
                            />
                            <ErrorMessage
                              name="birthday"
                              component="div"
                              className="ml-4 pt-1 text-red-500 text-sm"
                            />
                          </>
                        )}
                      </Field>
                      <ErrorMessage
                        name="birthday"
                        component="div"
                        className="ml-4 pt-1 text-red-500 text-sm"
                      />
                    </div>

                    <div className="relative mb-3">
                      <label className="text-md font-medium mb-2">
                        {t("profile.edit.gender")}
                      </label>
                      <Field
                        as="select"
                        name="gender"
                        onChange={(e: any) => {
                          setFieldValue("gender", e.target.value); // Manually update Formik state
                          console.log("Gender changed to:", e.target.value); // Debugging
                        }}
                        className="w-full px-4 py-3 border rounded-full text-md bg-white focus:outline-none focus:ring-2 focus:ring-theme appearance-none"
                      >
                        <option value="" disabled>
                          {t("profile.edit.gender")}
                        </option>
                        <option value="M">{t("profile.edit.male")}</option>
                        <option value="F">{t("profile.edit.female")}</option>
                      </Field>
                      <ErrorMessage
                        name="gender"
                        component="div"
                        className="ml-4 pt-1 text-red-500 text-sm"
                      />
                    </div>

                    <div className="relative mb-3">
                      <label className="text-md font-medium mb-2">
                        Address
                      </label>
                      <div className="">
                        <Field
                          type="text"
                          name="address1"
                          placeholder={t("profile.edit.line1")}
                          className="w-full px-4 py-3 border rounded-full text-md focus:outline-none focus:ring-2 focus:ring-theme"
                        />
                        <ErrorMessage
                          name="address1"
                          component="div"
                          className="ml-4 pt-1 text-red-500 text-sm"
                        />
                      </div>
                      <div className="mt-2">
                        <Field
                          type="text"
                          name="address2"
                          placeholder={t("profile.edit.line2")}
                          className="w-full px-4 py-3 border rounded-full text-md focus:outline-none focus:ring-2 focus:ring-theme"
                        />
                        <ErrorMessage
                          name="address2"
                          component="div"
                          className="ml-4 pt-1 text-red-500 text-sm"
                        />
                      </div>
                      <div className="mt-2">
                        <Field
                          type="text"
                          name="city"
                          placeholder={t("profile.edit.city")}
                          className="w-full px-4 py-3 border rounded-full text-md focus:outline-none focus:ring-2 focus:ring-theme"
                        />
                        <ErrorMessage
                          name="city"
                          component="div"
                          className="ml-4 pt-1 text-red-500 text-sm"
                        />
                      </div>
                      <div className="mt-2">
                        <Field
                          as="select"
                          name="state"
                          onChange={(e: any) => {
                            setFieldValue("state", e.target.value);
                          }}
                          className="w-full px-4 py-3 border rounded-full text-md bg-white focus:outline-none focus:ring-2 focus:ring-theme appearance-none"
                        >
                          <option value="" disabled>
                            {t("profile.edit.select_state")}
                          </option>
                          {statesList.map((state) => (
                            <option key={state.id} value={state.id}>
                              {state.state_name}
                            </option>
                          ))}
                        </Field>
                        <ErrorMessage
                          name="gender"
                          component="div"
                          className="ml-4 pt-1 text-red-500 text-sm"
                        />
                      </div>
                      <div className="mt-2">
                        <Field
                          type="text"
                          name="post_code"
                          placeholder={t("profile.edit.postcode")}
                          className="w-full px-4 py-3 border rounded-full text-md focus:outline-none focus:ring-2 focus:ring-theme"
                        />
                        <ErrorMessage
                          name="post_code"
                          component="div"
                          className="ml-4 pt-1 text-red-500 text-sm"
                        />
                      </div>
                    </div>

                    <button
                      type="button"
                      className="text-white text-[16px] leading-none w-full py-[15px] bg-theme border-none rounded-3xl flex items-center justify-center"
                      onClick={() => {
                        handleSubmit();
                      }}
                    >
                      {innerLoading ? (
                        <FaSpinner className="animate-spin" />
                      ) : (
                        t("profile.edit.save")
                      )}
                    </button>
                  </>
                )}
              </Form>
            )}
          </Formik>
        </div>
      </div>
    </>
  );
};

export default EditProfile;
